/**
 * Comment Modal Component
 * Displays lesson comments with lazy loading, star rating, and add comment form
 */

import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Star, Eye, EyeOff, MessageSquare, Send, ChevronDown, ChevronUp, Reply, Clock, XCircle, User } from 'lucide-react';
// Ensure Star is imported correctly
import {
  commentService,
  LessonCommentDto,
  LibraryBookCommentDto,
  TagBlockCommentDto
} from '../../services/commentService';
import { useAuth } from '../../hooks/useAuth';
import { authService } from '../../services/authService';
import { useNotifications } from '../feedback/NotificationSystem';
import { notifyGamificationAfterAction } from '../../utils/gamificationNotif';
import { useTheme } from '../../hooks/useTheme';
import { useLocale } from '../../hooks/useLocale';
import { CollapsibleFadePanel } from '../shared/CollapsibleFadePanel';
import { formatAppDateTime } from '../../utils/localeDateFormat';
import { apiService } from '../../services/api';
import { resolveDisplayProfilePhotoUrl } from '../../utils/profilePhotoUrl';
import {
  FLOATING_KEYBOARD_ANIM_MS,
  useFloatingKeyboardSlideReveal,
} from '../../utils/learnerSlideModalMotion';
import { prefersReducedMotion } from '../../utils/subscriptionAccessibility';
import { useHistoryBackClose } from '../../hooks/useHistoryBackClose';
import {
  buildCommentTree,
  commentThreadIndentStep,
  CommentThreadNode,
  countApprovedComments,
  normalizeCommentListPage
} from '../../utils/commentThread';

const COMMENT_MODAL_CLOSE_ANIM_MS = FLOATING_KEYBOARD_ANIM_MS;

function PublicLetterAvatar({ name, isDark }: { name?: string | null; isDark: boolean }) {
  const initial = (name || 'Anonymous').trim().charAt(0).toUpperCase() || 'A';
  return (
    <div className={`p-1.5 rounded-full shrink-0 ${isDark ? 'bg-gray-600' : 'bg-blue-100'}`}>
      <span className={`text-xs font-bold ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
        {initial}
      </span>
    </div>
  );
}

function ProfilePlaceholderAvatar() {
  return (
    <div className="flex h-6 w-6 shrink-0 items-center justify-center overflow-hidden rounded-full bg-gradient-to-br from-purple-400 to-pink-400">
      <User className="h-3.5 w-3.5 text-white" aria-hidden />
    </div>
  );
}

function CommentWriterAvatar({
  comment,
  isDark,
  currentUserId,
  currentUserPhotoUrl
}: {
  comment: LessonCommentDto | LibraryBookCommentDto;
  isDark: boolean;
  currentUserId?: string;
  currentUserPhotoUrl?: string;
}) {
  const [photoFailed, setPhotoFailed] = useState(false);

  const photoUrl = useMemo(() => {
    if (comment.isAnonymous) return undefined;
    const fromComment = resolveDisplayProfilePhotoUrl(comment.writerProfilePhotoUrl);
    if (fromComment) return fromComment;
    if (currentUserId && comment.userId === currentUserId) {
      return resolveDisplayProfilePhotoUrl(currentUserPhotoUrl);
    }
    return undefined;
  }, [comment.isAnonymous, comment.writerProfilePhotoUrl, comment.userId, currentUserId, currentUserPhotoUrl]);

  useEffect(() => {
    setPhotoFailed(false);
  }, [photoUrl]);

  if (comment.isAnonymous) {
    return <PublicLetterAvatar name={comment.writerName} isDark={isDark} />;
  }

  if (photoUrl && !photoFailed) {
    return (
      <img
        key={photoUrl}
        src={photoUrl}
        alt={comment.writerName || 'User'}
        className="h-6 w-6 shrink-0 rounded-full border border-gray-300 object-cover dark:border-gray-500"
        referrerPolicy="no-referrer"
        onError={() => setPhotoFailed(true)}
      />
    );
  }

  return <ProfilePlaceholderAvatar />;
}

interface CommentModalProps {
  lessonId?: number;
  libraryBookId?: number;
  tagId?: number;
  tagBlockId?: number;
  lessonTitle?: string;
  bookTitle?: string;
  tagTitle?: string;
  focusCommentId?: number;
  isOpen: boolean;
  onClose: () => void;
}

type ViewerComment = LessonCommentDto | LibraryBookCommentDto | TagBlockCommentDto;

export const CommentModal: React.FC<CommentModalProps> = ({
  lessonId,
  libraryBookId,
  tagId,
  tagBlockId,
  lessonTitle,
  bookTitle,
  tagTitle,
  focusCommentId,
  isOpen,
  onClose
}) => {
  const isTagMode = tagId != null && tagBlockId != null;
  const isBookMode = !isTagMode && libraryBookId != null;
  const contentId = isTagMode ? tagBlockId! : isBookMode ? libraryBookId! : lessonId!;
  const contentTitle = isTagMode ? tagTitle : isBookMode ? bookTitle : lessonTitle;
  // Use useAuth - it will return safe defaults if AuthProvider is missing
  const authContext = useAuth();
  const user = authContext.user;
  const authLoading = authContext.isLoading;
  
  // Check authentication: use both context and direct service check
  // This handles cases where user object might not be loaded yet but token is valid
  const isAuthenticated = authContext.isAuthenticated || (authService.isAuthenticated() && !authLoading);
  
  const { showError, showSuccess } = useNotifications();
  const { isDark } = useTheme();
  const { t } = useTranslation('learning');
  const { t: tCommon } = useTranslation('common');
  const { locale } = useLocale();

  const [comments, setComments] = useState<LessonCommentDto[]>([]);
  const [bookComments, setBookComments] = useState<LibraryBookCommentDto[]>([]);
  const [tagComments, setTagComments] = useState<TagBlockCommentDto[]>([]);
  const [userThreadLesson, setUserThreadLesson] = useState<LessonCommentDto[]>([]);
  const [userThreadBook, setUserThreadBook] = useState<LibraryBookCommentDto[]>([]);
  const [userThreadTag, setUserThreadTag] = useState<TagBlockCommentDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [commentTotalCount, setCommentTotalCount] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  // Add comment form state
  const [commentText, setCommentText] = useState('');
  const [rating, setRating] = useState<number | null>(null);
  const [isAnonymous, setIsAnonymous] = useState(false); // false = show name, true = hide name
  const [isFormExpanded, setIsFormExpanded] = useState(false); // Collapsed by default so comments are visible first
  const [replyToId, setReplyToId] = useState<number | null>(null);
  const [replyText, setReplyText] = useState('');
  const [replyAnonymous, setReplyAnonymous] = useState(false);
  const [replySubmitting, setReplySubmitting] = useState(false);
  const [collapsedThreadIds, setCollapsedThreadIds] = useState<Set<number>>(new Set());

  const commentsContainerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const isClosingRef = useRef(false);
  const finishCloseRef = useRef(false);
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);
  const pageSize = 10;

  const finishClose = useCallback(() => {
    if (finishCloseRef.current) return;
    finishCloseRef.current = true;
    isClosingRef.current = false;
    onClose();
    setMounted(false);
  }, [onClose]);

  const requestClose = useCallback(() => {
    if (finishCloseRef.current || isClosingRef.current) return;
    isClosingRef.current = true;
    setVisible(false);
    if (!visible) {
      window.requestAnimationFrame(() => {
        if (!finishCloseRef.current) finishClose();
      });
    }
  }, [visible, finishClose]);

  const onHistoryBack = useCallback(() => {
    if (finishCloseRef.current || isClosingRef.current) return true;
    requestClose();
    return false;
  }, [requestClose]);

  useHistoryBackClose(isOpen, onHistoryBack, 'comment-modal');

  useLayoutEffect(() => {
    if (isOpen) {
      finishCloseRef.current = false;
      isClosingRef.current = false;
      setIsFormExpanded(false);
      setReplyToId(null);
      setReplyText('');
      setReplyAnonymous(false);
      setCollapsedThreadIds(new Set());
      setMounted(true);
      setVisible(prefersReducedMotion());
      return undefined;
    }
    isClosingRef.current = true;
    setVisible(false);
    return undefined;
  }, [isOpen]);

  const revealPanel = useCallback(() => setVisible(true), []);
  useFloatingKeyboardSlideReveal(isOpen && mounted && !visible && !isClosingRef.current, revealPanel);

  const handleExitTransitionEnd = useCallback(
    (e: React.TransitionEvent<HTMLDivElement>) => {
      if (e.target !== e.currentTarget) return;
      if (e.propertyName !== 'opacity') return;
      if (visible || !isClosingRef.current) return;
      finishClose();
    },
    [visible, finishClose]
  );

  useEffect(() => {
    if (visible || !mounted || !isClosingRef.current) return undefined;
    const timer = window.setTimeout(finishClose, COMMENT_MODAL_CLOSE_ANIM_MS + 50);
    return () => window.clearTimeout(timer);
  }, [visible, mounted, finishClose]);

  useEffect(() => {
    if (!mounted) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') requestClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mounted, requestClose]);

  // Load initial comments
  useEffect(() => {
    if (isOpen && contentId) {
      apiService.clearCache();
      loadComments(1, true);
    } else {
      setUserThreadLesson([]);
      setUserThreadBook([]);
      setUserThreadTag([]);
    }
  }, [isOpen, contentId, isBookMode, isTagMode, isAuthenticated, user?.id]);

  const isApprovedComment = (comment: { isApproved?: boolean | null }) => comment.isApproved === true;

  const isOwnUnpublishedComment = (comment: { userId?: string; isApproved?: boolean | null }) =>
    !!user?.id && String(comment.userId) === String(user.id) && !isApprovedComment(comment);

  const loadUserThreadComments = async () => {
    if (!user?.id) {
      setUserThreadLesson([]);
      setUserThreadBook([]);
      setUserThreadTag([]);
      return;
    }

    try {
      if (isTagMode) {
        const result = await commentService.getMyTagBlockComments(
          {
            contentTagId: tagId,
            contentBlockId: tagBlockId,
            statusFilter: 'all',
            includeThreadReplies: true,
            pageNumber: 1,
            pageSize: 100
          },
          true
        );
        setUserThreadTag((result.comments ?? []) as TagBlockCommentDto[]);
        setUserThreadLesson([]);
        setUserThreadBook([]);
        return;
      }
      if (isBookMode) {
        const result = await commentService.getMyLibraryBookComments(
          {
            libraryBookId,
            statusFilter: 'all',
            includeThreadReplies: true,
            pageNumber: 1,
            pageSize: 100
          },
          true
        );
        setUserThreadBook((result.comments ?? (result as { Comments?: LibraryBookCommentDto[] }).Comments ?? []) as LibraryBookCommentDto[]);
        setUserThreadLesson([]);
        return;
      }

      const result = await commentService.getMyComments(
        {
          lessonId,
          statusFilter: 'all',
          includeThreadReplies: true,
          pageNumber: 1,
          pageSize: 100
        },
        true
      );
      setUserThreadLesson((result.comments ?? (result as { Comments?: LessonCommentDto[] }).Comments ?? []) as LessonCommentDto[]);
      setUserThreadBook([]);
      setUserThreadTag([]);
    } catch {
      setUserThreadLesson([]);
      setUserThreadBook([]);
      setUserThreadTag([]);
    }
  };

  const loadComments = async (page: number = 1, reset: boolean = false) => {
    try {
      if (reset) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      if (isTagMode) {
        const raw = await commentService.getTagBlockComments(tagId!, tagBlockId!, page, pageSize, true);
        const result = {
          comments: raw.comments ?? [],
          totalCount: raw.totalCount ?? 0,
          hasMore: raw.hasMore ?? false
        };
        if (reset) {
          setTagComments(result.comments);
          setComments([]);
          setBookComments([]);
        } else {
          setTagComments((prev) => [...prev, ...result.comments]);
        }
        setCommentTotalCount(result.totalCount);
        setHasMore(result.hasMore);
        setPageNumber(page);
        if (user?.id) await loadUserThreadComments();
        return;
      }

      if (isBookMode) {
        const raw = await commentService.getLibraryBookComments(
          libraryBookId!,
          page,
          pageSize,
          true
        );
        const result = normalizeCommentListPage<LibraryBookCommentDto>(
          raw as unknown as Record<string, unknown>
        );
        const approvedOnly = result.comments.filter(isApprovedComment);
        if (reset) {
          setBookComments(approvedOnly);
          await loadUserThreadComments();
        } else {
          setBookComments((prev) => {
            const seen = new Set(prev.map((c) => c.id));
            const next = [...prev];
            for (const comment of approvedOnly) {
              if (seen.has(comment.id)) continue;
              seen.add(comment.id);
              next.push(comment);
            }
            return next;
          });
        }
        setCommentTotalCount(result.totalCount ?? 0);
        setPageNumber(page);
        setHasMore(result.hasMore);
        if (reset) {
          setCollapsedThreadIds(new Set());
        }
        return;
      }

      const raw = await commentService.getLessonComments(
        lessonId!,
        page,
        pageSize,
        true
      );
      const result = normalizeCommentListPage<LessonCommentDto>(
        raw as unknown as Record<string, unknown>
      );
      const approvedOnly = result.comments.filter(isApprovedComment);
      if (reset) {
        setComments(approvedOnly);
        await loadUserThreadComments();
      } else {
        setComments((prev) => {
          const seen = new Set(prev.map((c) => c.id));
          const next = [...prev];
          for (const comment of approvedOnly) {
            if (seen.has(comment.id)) continue;
            seen.add(comment.id);
            next.push(comment);
          }
          return next;
        });
      }
      setCommentTotalCount(result.totalCount ?? 0);
      setPageNumber(page);
      setHasMore(result.hasMore);
      if (reset) {
        setCollapsedThreadIds(new Set());
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : t('comments.loadCommentsFailed');
      showError(message);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleScroll = useCallback(() => {
    if (!commentsContainerRef.current || loading || loadingMore || !hasMore) return;
    const container = commentsContainerRef.current;
    const { scrollTop, scrollHeight, clientHeight } = container;
    if (scrollTop + clientHeight >= scrollHeight * 0.8) {
      void loadComments(pageNumber + 1, false);
    }
  }, [loading, loadingMore, hasMore, pageNumber]);

  useEffect(() => {
    const container = commentsContainerRef.current;
    if (!container) return;
    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const handleSubmitComment = async () => {
    // Check authentication - wait for auth to finish loading before checking
    if (authLoading) {
      showError(t('comments.waitVerifyAuth'));
      return;
    }
    
    // Check authentication - use direct service check as fallback
    const tokenValid = authService.isAuthenticated();
    if (!tokenValid) {
      showError(t('comments.loginToComment'));
      return;
    }
    
    // If user object is not loaded yet, try to get it
    if (!user && tokenValid) {
      try {
        const profile = await authService.getProfile();
        // User will be available on next render, but we can proceed with submission
      } catch (error) {
        showError(t('comments.profileLoadFailed'));
        return;
      }
    }

    // Either comment text or rating is required (both are optional individually)
    if (!commentText.trim() && !rating) {
      showError(t('comments.provideCommentOrRating'));
      return;
    }

    if (commentText.length > 200) {
      showError(t('comments.commentTooLong'));
      return;
    }

    try {
      setSubmitting(true);
      const isRatingOnlySubmit = !commentText.trim() && !!rating;

      if (isTagMode) {
        const newComment = await commentService.createTagBlockComment({
          contentTagId: tagId!,
          contentBlockId: tagBlockId!,
          commentText: commentText.trim() || undefined,
          rating: rating || undefined,
          isAnonymous
        });
        showSuccess(
          isRatingOnlySubmit
            ? t('comments.ratingSavedVisible')
            : t('comments.commentSubmittedPending')
        );
        const userIdToNotify = user?.id || newComment.userId;
        if (userIdToNotify) {
          window.dispatchEvent(new CustomEvent('commentCreated', {
            detail: { userId: userIdToNotify, comment: newComment }
          }));
        }
      } else if (isBookMode) {
        const newComment = await commentService.createLibraryBookComment({
          libraryBookId: libraryBookId!,
          commentText: commentText.trim() || undefined,
          rating: rating || undefined,
          isAnonymous
        });
        showSuccess(
          isRatingOnlySubmit
            ? t('comments.ratingSavedVisible')
            : t('comments.commentSubmittedPending')
        );
        const userIdToNotify = user?.id || newComment.userId;
        if (userIdToNotify) {
          window.dispatchEvent(new CustomEvent('commentCreated', {
            detail: { userId: userIdToNotify, comment: newComment }
          }));
        }
      } else {
        const newComment = await commentService.createComment({
          lessonId: lessonId!,
          commentText: commentText.trim() || undefined,
          rating: rating || undefined,
          isAnonymous
        });
        showSuccess(
          isRatingOnlySubmit
            ? t('comments.ratingSavedVisible')
            : t('comments.commentSubmittedPending')
        );
        const userIdToNotify = user?.id || newComment.userId;
        if (userIdToNotify) {
          window.dispatchEvent(new CustomEvent('commentCreated', {
            detail: { userId: userIdToNotify, comment: newComment }
          }));
          window.dispatchEvent(new CustomEvent('newMessageSent', {
            detail: { userId: userIdToNotify }
          }));
        }
      }

      setCommentText('');
      setRating(null);
      setIsAnonymous(false);
      await loadComments(1, true);
      notifyGamificationAfterAction();
    } catch (error) {
      const message = error instanceof Error ? error.message : t('comments.commentSubmitFailed');
      showError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleStartReply = (commentId: number) => {
    if (authLoading) {
      showError(t('comments.waitVerifyAuth'));
      return;
    }
    if (!authService.isAuthenticated()) {
      showError(t('comments.loginToReply'));
      return;
    }
    setIsFormExpanded(false);
    setReplyToId(commentId);
    setReplyText('');
    setReplyAnonymous(false);
  };

  const handleSubmitReply = async (parent: ViewerComment) => {
    if (authLoading) {
      showError(t('comments.waitVerifyAuth'));
      return;
    }
    if (!authService.isAuthenticated()) {
      showError(t('comments.loginToReply'));
      return;
    }
    if (!replyText.trim()) {
      showError(t('comments.replyRequired'));
      return;
    }
    if (replyText.length > 200) {
      showError(t('comments.commentTooLong'));
      return;
    }

    try {
      setReplySubmitting(true);
      if (isTagMode) {
        await commentService.createTagBlockComment({
          contentTagId: tagId!,
          contentBlockId: tagBlockId!,
          parentCommentId: parent.id,
          commentText: replyText.trim(),
          isAnonymous: replyAnonymous
        });
        showSuccess(t('comments.replySubmittedPending'));
      } else if (isBookMode) {
        await commentService.createLibraryBookComment({
          libraryBookId: libraryBookId!,
          parentCommentId: parent.id,
          commentText: replyText.trim(),
          isAnonymous: replyAnonymous
        });
        showSuccess(t('comments.replySubmittedPending'));
      } else {
        const newComment = await commentService.createComment({
          lessonId: lessonId!,
          parentCommentId: parent.id,
          commentText: replyText.trim(),
          isAnonymous: replyAnonymous
        });
        showSuccess(t('comments.replySubmittedPending'));
        const userIdToNotify = user?.id || newComment.userId;
        if (userIdToNotify) {
          window.dispatchEvent(new CustomEvent('commentCreated', {
            detail: { userId: userIdToNotify, comment: newComment }
          }));
        }
      }
      setReplyToId(null);
      setReplyText('');
      setReplyAnonymous(false);
      await loadComments(1, true);
      notifyGamificationAfterAction();
    } catch (error) {
      const message = error instanceof Error ? error.message : t('comments.commentSubmitFailed');
      showError(message);
    } finally {
      setReplySubmitting(false);
    }
  };

  const formatDate = (dateString: string) => formatAppDateTime(dateString, locale);

  const renderStars = (value: number | null, interactive: boolean = false, onStarClick?: (value: number) => void) => {
    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => interactive && onStarClick && onStarClick(star)}
            disabled={!interactive || submitting}
            className={`${interactive ? 'cursor-pointer hover:scale-110' : 'cursor-default'} transition-transform ${
              value && star <= value
                ? 'text-yellow-400'
                : isDark
                ? 'text-gray-600'
                : 'text-gray-300'
            }`}
          >
            <Star
              className={`w-3.5 h-3.5 ${value && star <= value ? 'fill-current' : ''}`}
            />
          </button>
        ))}
      </div>
    );
  };

  /** Approved comments for everyone, plus the user's unpublished rows (not counted in the title). */
  const allCommentsMerged = useMemo(() => {
    if (!mounted) return [];
    const publicList = isTagMode ? tagComments : isBookMode ? bookComments : comments;
    const userThread = isTagMode ? userThreadTag : isBookMode ? userThreadBook : userThreadLesson;
    const seen = new Set<number>();
    const merged: ViewerComment[] = [];

    for (const comment of publicList) {
      if (!isApprovedComment(comment) || seen.has(comment.id)) continue;
      seen.add(comment.id);
      merged.push(comment);
    }
    for (const comment of userThread) {
      if (seen.has(comment.id)) continue;
      if (!isApprovedComment(comment) && !isOwnUnpublishedComment(comment)) continue;
      seen.add(comment.id);
      merged.push(comment);
    }

    return merged.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [mounted, isBookMode, isTagMode, comments, bookComments, tagComments, userThreadLesson, userThreadBook, userThreadTag, user?.id]);

  const commentTree = useMemo(
    () => buildCommentTree(allCommentsMerged),
    [allCommentsMerged]
  );

  /** Title count: approved roots, replies, and ratings for this lesson/book (excludes own pending/rejected). */
  const approvedTitleCount = useMemo(() => {
    if (commentTotalCount > 0) return commentTotalCount;
    const publicList = isTagMode ? tagComments : isBookMode ? bookComments : comments;
    return countApprovedComments(publicList);
  }, [commentTotalCount, isBookMode, isTagMode, bookComments, comments, tagComments]);

  useEffect(() => {
    if (!focusCommentId || allCommentsMerged.length === 0) return;
    const byId = new Map(allCommentsMerged.map((c) => [c.id, c]));
    const ancestorIds: number[] = [];
    let current = byId.get(focusCommentId);
    while (current?.parentCommentId != null) {
      ancestorIds.push(current.parentCommentId);
      current = byId.get(current.parentCommentId);
    }
    setCollapsedThreadIds((prev) => {
      const next = new Set(prev);
      for (const id of ancestorIds) next.delete(id);
      next.delete(focusCommentId);
      return next;
    });
  }, [focusCommentId, allCommentsMerged]);

  useEffect(() => {
    if (!focusCommentId || loading || allCommentsMerged.length === 0) return undefined;
    const timer = window.setTimeout(() => {
      const el = commentsContainerRef.current?.querySelector(
        `[data-comment-id="${focusCommentId}"]`
      );
      if (el instanceof HTMLElement) {
        el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }, 120);
    return () => window.clearTimeout(timer);
  }, [focusCommentId, loading, allCommentsMerged.length, commentTree.length]);

  const renderCommentNode = (
    node: CommentThreadNode<ViewerComment>,
    depth: number
  ): React.ReactNode => {
    const comment = node.comment;
    const ownUnpublished = isOwnUnpublishedComment(comment);
    const canReply = isApprovedComment(comment);
    const countDescendants = (children: Array<CommentThreadNode<ViewerComment>>): number =>
      children.reduce((sum, child) => sum + 1 + countDescendants(child.replies), 0);
    const replyCount = countDescendants(node.replies);
    const indentStep = commentThreadIndentStep(depth);
    const isReply = depth > 0 || !!comment.parentCommentId;
    const parentLabel = comment.parentWriterName || undefined;
    const hasReplies = node.replies.length > 0;
    const isCollapsed = hasReplies && collapsedThreadIds.has(comment.id);

    return (
      <div
        key={comment.id}
        data-comment-id={comment.id}
        className="min-w-0 max-w-full"
        style={indentStep > 0 ? { marginInlineStart: indentStep } : undefined}
      >
        <div
          className={`p-3 rounded-lg border transition-all hover:shadow-md min-w-0 ${
            isReply
              ? isDark
                ? 'bg-gray-800/70 border-s-[3px] border-s-blue-400/70 border-gray-600'
                : 'bg-blue-50/40 border-s-[3px] border-s-blue-400 border-gray-200'
              : isDark
                ? 'bg-gradient-to-br from-gray-700/80 to-gray-800/80 border-gray-600 hover:border-gray-500'
                : 'bg-gradient-to-br from-white to-gray-50/50 border-gray-200 hover:border-blue-200'
          }`}
          onClick={(e) => {
            if (!(isReply && hasReplies)) return;
            const target = e.target as HTMLElement;
            if (target.closest('button,textarea,input,a,[role="button"]')) return;
            setCollapsedThreadIds((prev) => {
              const next = new Set(prev);
              if (next.has(comment.id)) next.delete(comment.id);
              else next.add(comment.id);
              return next;
            });
          }}
        >
          <div className="flex items-start justify-between mb-2">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <CommentWriterAvatar
                  comment={comment}
                  isDark={isDark}
                  currentUserId={user?.id}
                  currentUserPhotoUrl={user?.profilePhoto}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`font-semibold text-sm ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                      {comment.writerName || t('comments.anonymous')}
                    </span>
                    {ownUnpublished && (
                      comment.isApproved === false ? (
                        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-semibold rounded ${
                          isDark ? 'bg-red-900/40 text-red-200 border border-red-700/50' : 'bg-red-50 text-red-800 border border-red-200'
                        }`}>
                          <XCircle className="w-3 h-3" />
                          {t('comments.rejected')}
                        </span>
                      ) : (
                        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-semibold rounded ${
                          isDark ? 'bg-yellow-900/40 text-yellow-200 border border-yellow-700/50' : 'bg-yellow-50 text-yellow-800 border border-yellow-200'
                        }`}>
                          <Clock className="w-3 h-3" />
                          {t('comments.pending')}
                        </span>
                      )
                    )}
                    {comment.isAdminReply && (
                      <span className={`px-1.5 py-0.5 text-[10px] font-semibold rounded-full ${
                        isDark ? 'bg-purple-900/50 text-purple-200 border border-purple-700' : 'bg-purple-100 text-purple-800 border border-purple-300'
                      }`}>
                        {t('comments.adminBadge')}
                      </span>
                    )}
                    {comment.ratingOnly && (
                      <span className={`px-1.5 py-0.5 text-[10px] font-semibold rounded ${
                        isDark ? 'bg-amber-900/40 text-amber-200 border border-amber-700/50' : 'bg-amber-50 text-amber-800 border border-amber-200'
                      }`}>
                        {t('comments.ratingOnly')}
                      </span>
                    )}
                    {replyCount > 0 && (
                      <button
                        type="button"
                        onClick={() =>
                          setCollapsedThreadIds((prev) => {
                            const next = new Set(prev);
                            if (next.has(comment.id)) next.delete(comment.id);
                            else next.add(comment.id);
                            return next;
                          })
                        }
                        className={`px-1.5 py-0.5 text-[10px] font-semibold rounded inline-flex items-center gap-1 transition-colors ${
                        isDark ? 'bg-indigo-900/50 text-indigo-200 border border-indigo-700/50' : 'bg-indigo-50 text-indigo-800 border border-indigo-200'
                      }`}>
                        <MessageSquare className="w-3 h-3" />
                        {replyCount}
                        {hasReplies && (
                          <ChevronDown
                            className={`w-3 h-3 transition-transform ${isCollapsed ? '-rotate-90' : 'rotate-0'}`}
                          />
                        )}
                      </button>
                    )}
                  </div>
                  {comment.rating && (
                    <div className="flex items-center mt-0.5">
                      {renderStars(comment.rating, false)}
                    </div>
                  )}
                </div>
              </div>
              <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'} ms-8`}>
                {formatDate(comment.createdAt)}
                {isReply && parentLabel && (
                  <span className={`ms-1.5 ${isDark ? 'text-blue-300' : 'text-blue-600'}`}>
                    · {t('comments.inReplyTo', { name: parentLabel })}
                  </span>
                )}
              </p>
            </div>
          </div>
          {comment.commentText && (
            <div className={`mt-2 p-2.5 rounded ${isDark ? 'bg-gray-800/50' : 'bg-gray-50'}`}>
              <p className={`text-xs leading-relaxed break-words ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                {comment.commentText}
              </p>
            </div>
          )}
          {canReply && (
          <div className="mt-2 flex items-center">
            <button
              type="button"
              onClick={() => (replyToId === comment.id ? setReplyToId(null) : handleStartReply(comment.id))}
              className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition-colors ${
                replyToId === comment.id
                  ? isDark
                    ? 'bg-blue-600/30 text-blue-200'
                    : 'bg-blue-100 text-blue-700'
                  : isDark
                    ? 'text-blue-300 hover:bg-gray-700'
                    : 'text-blue-700 hover:bg-blue-50'
              }`}
            >
              <Reply className="w-3.5 h-3.5" />
              {t('comments.reply')}
            </button>
          </div>
          )}
          {canReply && (
          <div
            className={`overflow-hidden transition-all ${
              prefersReducedMotion() ? '' : 'duration-200 ease-out'
            } ${
              replyToId === comment.id
                ? 'max-h-[340px] opacity-100 mt-2'
                : 'max-h-0 opacity-0 mt-0 pointer-events-none'
            }`}
            aria-hidden={replyToId !== comment.id}
          >
            <div className={`p-2.5 rounded-lg space-y-2 ${isDark ? 'bg-gray-900/60 border border-gray-600' : 'bg-white border border-blue-100'}`}>
              <p className={`text-[11px] font-medium ${isDark ? 'text-blue-300' : 'text-blue-700'}`}>
                {t('comments.replyingTo', { name: comment.writerName || t('comments.anonymous') })}
              </p>
              <textarea
                value={replyText}
                onChange={(e) => {
                  if (e.target.value.length <= 200) setReplyText(e.target.value);
                }}
                placeholder={t('comments.replyPlaceholder')}
                rows={2}
                maxLength={200}
                className={`w-full px-2.5 py-2 border rounded-lg resize-none text-xs min-h-[52px] ${
                  isDark
                    ? 'bg-gray-700/80 border-gray-600 text-gray-100 placeholder-gray-400 focus:border-blue-500'
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-blue-500'
                } focus:outline-none focus:ring-2 focus:ring-blue-500/20`}
              />
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <span className={`text-[10px] ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  {replyText.length}/200
                </span>
                <button
                  type="button"
                  onClick={() => setReplyAnonymous(!replyAnonymous)}
                  className={`inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] ${
                    replyAnonymous
                      ? isDark
                        ? 'bg-blue-600 text-white'
                        : 'bg-blue-100 text-blue-700'
                      : isDark
                        ? 'bg-gray-700 text-gray-300'
                        : 'bg-gray-100 text-gray-700'
                  }`}
                >
                  {replyAnonymous ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                  {replyAnonymous ? t('comments.anonymous') : t('comments.showName')}
                </button>
              </div>
              <div className="flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setReplyToId(null);
                    setReplyText('');
                    setReplyAnonymous(false);
                  }}
                  className={`px-3 py-1.5 rounded-lg text-xs ${
                    isDark ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {t('comments.cancelReply')}
                </button>
                <button
                  type="button"
                  onClick={() => void handleSubmitReply(comment)}
                  disabled={replySubmitting || !replyText.trim()}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold inline-flex items-center gap-1 ${
                    replySubmitting || !replyText.trim()
                      ? isDark
                        ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : isDark
                        ? 'bg-blue-600 text-white hover:bg-blue-700'
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  <Send className="w-3.5 h-3.5" />
                  {replySubmitting ? t('comments.submitting') : t('comments.submitReply')}
                </button>
              </div>
            </div>
          </div>
          )}
          {node.replies.length > 0 && (
          <CollapsibleFadePanel
            expanded={!isCollapsed}
            className={isCollapsed ? 'mt-0' : 'mt-2'}
            innerClassName="space-y-2"
          >
            {node.replies.map((child) => renderCommentNode(child, depth + 1))}
          </CollapsibleFadePanel>
          )}
        </div>
      </div>
    );
  };

  if (!mounted) return null;

  return (
    <div className="learner-slide-modal-root fixed inset-0 z-[200] py-6 sm:py-10" role="presentation">
      <div
        className={`keyboard-slide-modal-backdrop fixed inset-0 bg-black/45 backdrop-blur-[2px] dark:bg-black/55${
          visible ? ' is-visible' : ''
        }`}
        aria-hidden="true"
        onClick={requestClose}
      />
      <div className="flex min-h-full items-end justify-center px-3 sm:px-6">
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="comment-modal-title"
          className={`learner-calm-modal keyboard-slide-modal-panel keyboard-slide-modal-panel--slide-up pointer-events-auto relative flex h-[min(90vh,820px)] w-full max-w-4xl flex-col overflow-hidden rounded-t-2xl border shadow-2xl sm:rounded-2xl${
            isDark
              ? ' border-gray-700 bg-gradient-to-br from-gray-800 to-gray-900'
              : ' border-gray-200 bg-gradient-to-br from-white to-gray-50'
          }${visible ? ' is-visible' : ''}`}
          onTransitionEnd={handleExitTransitionEnd}
          onClick={(e) => e.stopPropagation()}
        >
        {/* Header */}
        <div className={`${isDark ? 'bg-gradient-to-r from-gray-700 to-gray-800' : 'bg-gradient-to-r from-blue-50 to-indigo-50'} shrink-0 px-4 sm:px-6 py-4 border-b ${isDark ? 'border-gray-600' : 'border-gray-200'} flex items-center justify-between gap-2`}>
          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
            <div className={`p-1.5 sm:p-2 rounded-lg shrink-0 ${isDark ? 'bg-gray-700' : 'bg-blue-100'}`}>
              <MessageSquare className={`w-5 h-5 sm:w-6 sm:h-6 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
            </div>
            <div className="min-w-0">
              <h2 id="comment-modal-title" className={`text-base sm:text-2xl font-bold truncate ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                {t('comments.title')}
                <span className={`ms-2 font-semibold ${isDark ? 'text-blue-300' : 'text-blue-600'}`}>
                  ({approvedTitleCount})
                </span>
              </h2>
              {contentTitle && (
                <p className={`text-xs sm:text-sm truncate ${isDark ? 'text-gray-400' : 'text-gray-600'} mt-0.5`}>
                  {contentTitle}
                </p>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={requestClose}
            className={`p-2 rounded-lg transition-colors ${
              isDark
                ? 'hover:bg-gray-600 text-gray-300'
                : 'hover:bg-gray-200 text-gray-600'
            }`}
            aria-label={tCommon('close')}
            title={tCommon('close')}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Add Comment Form - Collapsible (collapsed by default) */}
        <div className={`${isDark ? 'bg-gray-800/50' : 'bg-gradient-to-b from-white to-gray-50/50'} shrink-0 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'} backdrop-blur-sm transition-all duration-200 ease-out`}>
          {/* Form Header with Expand/Collapse - Entire line is clickable */}
          <button
            onClick={() => setIsFormExpanded(!isFormExpanded)}
            className={`w-full px-4 sm:px-6 py-2 flex items-center justify-between ${!isFormExpanded ? 'border-b' : ''} ${isDark ? 'border-gray-700' : 'border-gray-200'} transition-all duration-200 ease-out ${isDark ? 'hover:bg-gray-700/50' : 'hover:bg-gray-100/50'} cursor-pointer`}
            aria-label={isFormExpanded ? t('comments.collapseForm') : t('comments.expandForm')}
          >
            <h3 className={`text-sm font-semibold ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              {t('comments.addComment')}
            </h3>
            <div className={`transition-all duration-200 ease-out transform ${isFormExpanded ? 'rotate-0' : 'rotate-180'} ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              <ChevronUp className="w-5 h-5 transition-transform duration-200 ease-out" />
            </div>
          </button>
          
          <div 
            className={`overflow-hidden transition-all duration-200 ease-out ${
              isFormExpanded ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
            }`}
          >
            <div className="px-4 sm:px-6 py-2.5 space-y-3">
            {/* Rate + stars on one row */}
            <div className={`p-2 rounded-lg ${isDark ? 'bg-gray-700/50' : 'bg-blue-50/50'}`}>
              <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
                <label className={`text-xs font-semibold shrink-0 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                  ⭐ {t('comments.rateOptional')}
                </label>
                <div className="flex items-center shrink-0">
                  {renderStars(rating, true, (value) => setRating(value === rating ? null : value))}
                </div>
              </div>
            </div>

            {/* Comment text */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className={`block text-xs font-semibold ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                  💬 {t('comments.commentLabel')}
                </label>
                <span className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  {commentText.length}/200
                </span>
              </div>
              <textarea
                value={commentText}
                onChange={(e) => {
                  if (e.target.value.length <= 200) {
                    setCommentText(e.target.value);
                  }
                }}
                placeholder={t('comments.commentPlaceholder')}
                rows={3}
                maxLength={200}
                className={`w-full px-3 py-2 border-2 rounded-lg resize-none transition-all text-sm min-h-[68px] ${
                  isDark
                    ? 'bg-gray-700/80 border-gray-600 text-gray-100 placeholder-gray-400 focus:border-blue-500'
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-blue-500'
                } focus:outline-none focus:ring-2 focus:ring-blue-500/20`}
              />
            </div>

            {/* Anonymous toggle: label may wrap (~2 lines); button stays vertically centered on the right */}
            <div className={`p-1.5 sm:p-2 rounded-lg ${isDark ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
              <div className="flex flex-row items-center gap-2 sm:gap-3">
                <span
                  className={`flex-1 min-w-0 text-[11px] sm:text-xs leading-snug ${isDark ? 'text-gray-300' : 'text-gray-600'}`}
                >
                  {isAnonymous 
                    ? t('comments.anonymousPublish') 
                    : t('comments.namedPublish')}
                </span>
                <button
                  type="button"
                  onClick={() => setIsAnonymous(!isAnonymous)}
                  className={`flex items-center justify-center gap-1.5 px-2.5 py-1.5 sm:px-3 sm:py-2 rounded-lg transition-all text-xs shrink-0 self-center ${
                    !isAnonymous
                      ? isDark
                        ? 'bg-gray-600/50 text-gray-300 hover:bg-gray-600 border border-gray-600'
                        : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                      : isDark
                      ? 'bg-blue-600 text-white border border-blue-500'
                      : 'bg-blue-100 text-blue-700 border border-blue-300'
                  }`}
                >
                  {isAnonymous ? (
                    <EyeOff className="w-4 h-4 shrink-0" />
                  ) : (
                    <Eye className="w-4 h-4 shrink-0" />
                  )}
                  <span className="text-xs font-medium whitespace-nowrap">
                    {isAnonymous ? t('comments.anonymous') : t('comments.showName')}
                  </span>
                </button>
              </div>
            </div>

            {/* Submit Button */}
            {authLoading ? (
              <div className={`w-full px-4 py-3 rounded-xl text-center font-medium ${isDark ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-600'}`}>
                {t('comments.verifyingAuth')}
              </div>
            ) : !isAuthenticated ? (
              <div className={`w-full px-4 py-3 rounded-xl text-center font-medium ${isDark ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-600'}`}>
                {t('comments.loginToComment')}
              </div>
            ) : (
              <button
                onClick={handleSubmitComment}
                disabled={submitting || (!commentText.trim() && !rating)}
                className={`w-full px-6 py-3 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all shadow-lg ${
                  submitting || (!commentText.trim() && !rating)
                    ? isDark
                      ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : isDark
                    ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:from-blue-700 hover:to-blue-800 hover:shadow-xl transform hover:scale-[1.02]'
                    : 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700 hover:shadow-xl transform hover:scale-[1.02]'
                }`}
              >
                <Send className="w-5 h-5" />
                {submitting ? t('comments.submitting') : t('comments.submitComment')}
              </button>
            )}
            </div>
          </div>
        </div>

        {/* Comments List - Scrollable - Increased height */}
        <div
          ref={commentsContainerRef}
          className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-y-contain px-3 sm:px-6 py-4 transition-all duration-200 ease-out"
        >
              {loading ? (
                <div className={`text-center py-8 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  {t('comments.loadingComments')}
                </div>
              ) : allCommentsMerged.length === 0 ? (
                <div className={`text-center py-8 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  <MessageSquare className={`w-12 h-12 mx-auto mb-4 ${isDark ? 'text-gray-600' : 'text-gray-400'}`} />
                  <p>{t('comments.noComments')}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {commentTree.map((node) => renderCommentNode(node, 0))}
              
              {loadingMore && (
                <div className={`text-center py-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  {t('comments.loadingMore')}
                </div>
              )}
              
              {!hasMore && allCommentsMerged.length > 0 && (
                <div className={`text-center py-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  {t('comments.noMoreComments')}
                </div>
              )}
            </div>
          )}
        </div>
        </div>
      </div>
    </div>
  );
};

